import React, { useEffect } from 'react';
import clsx from 'clsx';
import { shallow } from 'zustand/shallow';

import usePurchaseStore from 'stores/purchase';
import useUpcomingGameStore from 'stores/upcoming-game';
import useGameStatsStore from 'stores/current-game';
import { text } from 'polyglot/polyglot';
import { bool, number } from 'prop-types';

import styles from './BuyLotteryTicketButtons.module.scss';

const selectedCardIdsSelector = (state) => [
  state.selectedCardIds,
  state.updateSelectedCardIds,
];
const setUpcomingRoundIdSelector = (state) => state.setUpcomingRoundId;
const bingoCardsListSelector = (state) => [state.bingoCardsList];
const upcomingGameSelector = (state) => state.nextGame;
const currentGameSelector = (state) => state.currentGame;
const prebuyTicketAmountSelector = (state) => [
  state.prebuyTicketAmount,
  state.updatePrebuyTicketAmount,
];
// const ticketsNextGameSelector = (state) => state.numberOfTicketsNextGame;
const ticketsCurrentGameSelector = (state) => state.numberOfTicketsCurrentGame;

function BuyLotteryTicketButtons({
  isPrebuy,
  bingoRoundId,
  maxNumberOfCards,
  cardsAlreadyBought,
  isMobileExpanded,
}) {
  const [selectedTicketIds, setSelectedTicketIds] = usePurchaseStore(
    selectedCardIdsSelector,
    shallow
  );
  const [bingoCardsList] = usePurchaseStore(bingoCardsListSelector);
  const availableCards = bingoCardsList.filter((card) => !card.isPurchased);
  const unselectedCards = availableCards.filter(
    (card) => !selectedTicketIds.includes(card.id)
  );
  const [prebuyTicketAmount, setPrebuyTicketAmount] = usePurchaseStore(
    prebuyTicketAmountSelector,
    shallow
  );
  const nextGame = useUpcomingGameStore(upcomingGameSelector);
  const currentGame = useGameStatsStore(currentGameSelector);
  const setUpcomingRoundId = usePurchaseStore(setUpcomingRoundIdSelector);

  const numberOfTicketsCurrentGame = usePurchaseStore(
    ticketsCurrentGameSelector
  );
  // const numberOfTicketsNextGame = usePurchaseStore(ticketsNextGameSelector);

  const effectiveMaxCards = isPrebuy
    ? maxNumberOfCards ?? nextGame.maxNumberOfCards
    : currentGame.maxNumberOfCards;

  const remainingPrebuyTickets = isPrebuy
    ? effectiveMaxCards - (cardsAlreadyBought ?? 0)
    : currentGame.maxNumberOfCards - numberOfTicketsCurrentGame;

  const removeOneTicket = () => {
    if (isPrebuy && prebuyTicketAmount > 0) {
      setPrebuyTicketAmount(prebuyTicketAmount - 1);
    }
    if (!isPrebuy && selectedTicketIds.length) {
      setSelectedTicketIds(selectedTicketIds[selectedTicketIds.length - 1]);
    }
  };

  const addOneTicket = () => {
    if (
      isPrebuy &&
      prebuyTicketAmount < effectiveMaxCards - (cardsAlreadyBought ?? 0)
    ) {
      setPrebuyTicketAmount(prebuyTicketAmount + 1);
    }
    if (
      !isPrebuy &&
      unselectedCards.length &&
      numberOfTicketsCurrentGame + selectedTicketIds.length <
        currentGame.maxNumberOfCards
    ) {
      setSelectedTicketIds(unselectedCards[0].id);
    }
  };

  const updateNumberOfTickets = async (numberOfCards) => {
    if (numberOfCards === selectedTicketIds.length) {
      return;
    }
    if (numberOfCards > selectedTicketIds.length) {
      const difference = numberOfCards - selectedTicketIds.length;
      const idsToSelect = [];
      unselectedCards.map((card, index) => {
        if (index < difference) {
          idsToSelect.push(card.id);
        }
        return null;
      });
      idsToSelect.map((id) => setSelectedTicketIds(id));
    } else {
      const difference = selectedTicketIds.length - numberOfCards;
      selectedTicketIds.reverse().map((card, index) => {
        if (index < difference) {
          setSelectedTicketIds(card);
        }
        return null;
      });
    }
  };

  useEffect(() => {
    setUpcomingRoundId(bingoRoundId);
  }, [bingoRoundId, setUpcomingRoundId]);

  return (
    <div
      className={clsx(styles.wrapper, {
        [styles.expanded]: isMobileExpanded,
      })}
    >
      <span className={styles.title}>
        {text.t('buyLotteryTicketButtons.heading')}
      </span>
      <div className={styles.firstSection}>
        <button
          type="button"
          className={clsx(styles.payButton, styles.minusButton)}
          disabled={isPrebuy ? !prebuyTicketAmount : !selectedTicketIds.length}
          onClick={removeOneTicket}
        >
          {text.t('buyLotteryTicketButtons.minusButton')}
        </button>
        <div className={clsx(styles.payButton, styles.displayWrapper)}>
          <div className={styles.displayAmount}>
            {isPrebuy ? prebuyTicketAmount : selectedTicketIds.length}
          </div>
          <div className={styles.suffix}>
            {text.t('buyLotteryTicketButtons.suffix')}
          </div>
        </div>
        <button
          type="button"
          className={clsx(styles.payButton, styles.plusButton)}
          disabled={
            isPrebuy
              ? prebuyTicketAmount >= remainingPrebuyTickets
              : !unselectedCards.length
          }
          onClick={addOneTicket}
        >
          {text.t('buyLotteryTicketButtons.plusButton')}
        </button>
      </div>
      <div className={styles.secondSection}>
        <button
          type="button"
          className={clsx(styles.payButton, styles.payButtonWrapper)}
          disabled={
            isPrebuy
              ? (cardsAlreadyBought ?? 0) + 2 > effectiveMaxCards
              : numberOfTicketsCurrentGame + 2 > currentGame.maxNumberOfCards
          }
          onClick={() => {
            if (isPrebuy) {
              setPrebuyTicketAmount(2);
            } else {
              updateNumberOfTickets(2);
            }
          }}
        >
          <div className={styles.predefinedButtonAmount}>{2}</div>
          <div className={styles.suffix}>
            {text.t('buyLotteryTicketButtons.suffix')}
          </div>
        </button>
        <button
          type="button"
          className={clsx(styles.payButton, styles.payButtonWrapper)}
          disabled={
            isPrebuy
              ? (cardsAlreadyBought ?? 0) + 4 > effectiveMaxCards
              : numberOfTicketsCurrentGame + 4 > currentGame.maxNumberOfCards
          }
          onClick={() => {
            if (isPrebuy) {
              setPrebuyTicketAmount(4);
            } else {
              updateNumberOfTickets(4);
            }
          }}
        >
          <div className={styles.predefinedButtonAmount}>{4}</div>
          <div className={styles.suffix}>
            {text.t('buyLotteryTicketButtons.suffix')}
          </div>
        </button>
        <button
          type="button"
          className={styles.payButton}
          disabled={
            isPrebuy
              ? prebuyTicketAmount + (cardsAlreadyBought ?? 0) >=
                effectiveMaxCards
              : selectedTicketIds.length + numberOfTicketsCurrentGame >=
                currentGame.maxNumberOfCards
          }
          onClick={() => {
            if (isPrebuy) {
              setPrebuyTicketAmount(remainingPrebuyTickets);
            } else {
              if (
                currentGame.maxNumberOfCards - numberOfTicketsCurrentGame >
                availableCards.length
              )
                return;
              updateNumberOfTickets(
                currentGame.maxNumberOfCards - numberOfTicketsCurrentGame
              );
            }
          }}
        >
          {' '}
          <span className={styles.maxButtonWrapper}>
            <div className={styles.maxButtonAmount}>
              {effectiveMaxCards || currentGame.maxNumberOfCards}
            </div>

            <div className={styles.maxButtonText}>
              {text.t('buyLotteryTicketButtons.maxButton')}
            </div>
          </span>
        </button>
      </div>
    </div>
  );
}

BuyLotteryTicketButtons.propTypes = {
  isPrebuy: bool.isRequired,
  bingoRoundId: number,
  maxNumberOfCards: number,
  cardsAlreadyBought: number,
  isMobileExpanded: bool,
};

BuyLotteryTicketButtons.defaultProps = {
  bingoRoundId: null,
  maxNumberOfCards: null,
  cardsAlreadyBought: null,
  isMobileExpanded: false,
};

export default BuyLotteryTicketButtons;
